import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Lock } from 'lucide-react';
import { Button } from '../components/common/Button';
import { authApi } from '../services/api';
import restauragroLogo from '../assets/restauragro-logo.png';
import toast from 'react-hot-toast';

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;

    if (password.length < 6) {
      toast.error('A senha deve ter no mínimo 6 caracteres');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }

    setLoading(true);
    try {
      await authApi.resetPassword(token, password);
      toast.success('Senha redefinida com sucesso! Faça login com a nova senha.');
      navigate('/login');
    } catch (error: any) {
      const message = error.response?.data?.message || 'Não foi possível redefinir a senha.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-[#F7F9FF] px-4 py-12">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <img src={restauragroLogo} alt="RestaurAgro" className="mx-auto h-16 w-16 object-contain" />
        </div>

        <div className="rounded-2xl border border-[#D0D4E4] bg-white p-8 shadow-sm">
          {!token ? (
            <div className="text-center">
              <h2 className="text-2xl font-bold text-[#323338]">Link inválido</h2>
              <p className="mt-2 text-sm text-[#676879]">
                Este link de redefinição de senha é inválido ou está incompleto.
              </p>
              <Link
                to="/forgot-password"
                className="mt-6 inline-block text-sm font-medium text-[#C24914] hover:underline"
              >
                Solicitar um novo link
              </Link>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-[#323338]">Redefinir senha</h2>
              <p className="mt-1 text-sm text-[#676879]">Escolha uma nova senha para sua conta.</p>

              <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                <div>
                  <label htmlFor="password" className="mb-1 block text-sm font-medium text-[#323338]">Nova senha</label>
                  <div className="relative">
                    <Lock size={18} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#676879]" />
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      className="w-full rounded-lg border border-[#D0D4E4] py-3 pl-10 pr-12 text-sm focus:border-[#C24914] focus:outline-none focus:ring-2 focus:ring-[#C24914]/20"
                      placeholder="••••••••"
                      autoComplete="new-password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#676879] hover:text-[#323338]"
                      aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="mb-1 block text-sm font-medium text-[#323338]">Confirmar nova senha</label>
                  <div className="relative">
                    <Lock size={18} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#676879]" />
                    <input
                      id="confirmPassword"
                      type={showPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={e => setConfirmPassword(e.target.value)}
                      className="w-full rounded-lg border border-[#D0D4E4] py-3 pl-10 pr-4 text-sm focus:border-[#C24914] focus:outline-none focus:ring-2 focus:ring-[#C24914]/20"
                      placeholder="••••••••"
                      autoComplete="new-password"
                      required
                    />
                  </div>
                </div>

                <Button type="submit" variant="brand" loading={loading} className="w-full justify-center py-3">
                  Redefinir senha
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
